import { Alert, CircularProgress } from '@mui/material'
import type { ComponentChildren } from 'preact'
import { useEffect } from 'preact/hooks'
import { lazy, Route, Router, useRoute } from 'preact-iso'
import { api } from '../api/client'
import type { EventDetails } from '../api/generated/EventDetails'
import { useApi } from '../api/useApi'
import { PasskeyOffer } from '../auth/PasskeyOffer'
import { useVolunteerThreads, VolunteerThreadsProvider } from '../conversations/VolunteerThreads'
import { EventContext } from '../events/EventContext'
import { EventShell } from '../events/EventShell'
import { HelpBanner } from '../help/HelpBanner'
import { HelpProvider } from '../help/HelpContext'
import { OrganizerDataProvider } from '../organizer/OrganizerData'
import { OrganizerShell } from '../organizer/OrganizerShell'
import { unreadMessages } from '../organizer/overview'
import { AnnouncementsPage } from './AnnouncementsPage'
import { DashboardPage } from './DashboardPage'
import { NotFound } from './NotFound'
import { PositionNew } from './PositionNew'
import { PositionsPage } from './PositionsPage'
import { SettingsPage } from './SettingsPage'
import { MyAvailabilityPage, MyPlanningPage, VolunteerHome, WelcomePage } from './VolunteerPages'
import { VolunteersPage } from './VolunteersPage'

// Split out with the Markdown renderer, which would grow the main bundle by a third
const PositionPage = lazy(() => import('./PositionPage').then((m) => m.PositionPage))
const MessagesPage = lazy(() => import('./MessagesPage').then((m) => m.MessagesPage))
const PlanningPage = lazy(() => import('./PlanningPage').then((m) => m.PlanningPage))
const StatsPage = lazy(() => import('./StatsPage').then((m) => m.StatsPage))

const passkeyOffer = { mt: 2, mx: { xs: 2, sm: 'auto' }, maxWidth: 'sm' } as const

function OrganizerSpace({ slug }: { slug: string }) {
  return (
    <OrganizerDataProvider slug={slug}>
      <OrganizerShell>
        <PasskeyOffer sx={passkeyOffer} />
        <HelpBanner />
        <Router>
          <Route path="/" component={DashboardPage} />
          <Route path="/welcome" component={DashboardPage} />
          <Route path="/planning" component={PlanningPage} />
          <Route path="/volunteers" component={VolunteersPage} />
          <Route path="/messages" component={MessagesPage} />
          <Route path="/announcements" component={AnnouncementsPage} />
          <Route path="/positions" component={PositionsPage} />
          <Route path="/positions/new" component={PositionNew} />
          <Route path="/positions/:id" component={PositionPage} />
          <Route path="/stats" component={StatsPage} />
          <Route path="/settings" component={SettingsPage} />
          <Route path="/conversations/:volunteerId" component={MessagesPage} />
          <Route path="/groups/:groupId" component={MessagesPage} />
          <Route default component={NotFound} />
        </Router>
      </OrganizerShell>
    </OrganizerDataProvider>
  )
}

function VolunteerShell({ children }: { children: ComponentChildren }) {
  const { conversations, groups } = useVolunteerThreads()
  return (
    <EventShell badges={{ unread: unreadMessages(conversations.data ?? [], groups.data ?? []) }}>
      {children}
    </EventShell>
  )
}

function VolunteerSpace({ slug }: { slug: string }) {
  return (
    <VolunteerThreadsProvider slug={slug}>
      <VolunteerShell>
        <PasskeyOffer sx={passkeyOffer} />
        <HelpBanner />
        <Router>
          <Route path="/" component={VolunteerHome} />
          <Route path="/planning" component={MyPlanningPage} />
          <Route path="/messages" component={MessagesPage} />
          <Route path="/availability" component={MyAvailabilityPage} />
          <Route path="/welcome" component={WelcomePage} />
          <Route path="/positions" component={PositionsPage} />
          <Route path="/positions/:id" component={PositionPage} />
          <Route path="/conversations/:volunteerId" component={MessagesPage} />
          <Route path="/groups/:groupId" component={MessagesPage} />
          <Route default component={NotFound} />
        </Router>
      </VolunteerShell>
    </VolunteerThreadsProvider>
  )
}

/**
 * Pages under `/events/{slug}`: loads the event once for all of them, then the organizer space
 * or the volunteer's pages, by role.
 */
export function EventSpace() {
  const { params } = useRoute()
  const slug = params.slug ?? ''
  const { data: event, setData, error, reload } = useApi<EventDetails>(`/events/${slug}`)
  const mustJoin = event?.slug === slug && !event.joined

  // Opening the event consents to organizers seeing the profile; retried on next open if it fails
  useEffect(() => {
    if (!mustJoin) return
    let current = true
    api.post<EventDetails>(`/events/${slug}/join`, {}).then(
      (joined) => current && setData(joined),
      () => {},
    )
    return () => {
      current = false
    }
  }, [mustJoin, slug])

  if (error)
    return (
      <Alert severity="error" sx={{ m: 4 }}>
        {error}
      </Alert>
    )
  if (event?.slug !== slug) return <CircularProgress sx={{ m: 4 }} />

  return (
    <EventContext.Provider key={slug} value={{ event, setEvent: setData, reload }}>
      <HelpProvider slug={slug}>
        {event.role === 'organizer' ? (
          <OrganizerSpace slug={slug} />
        ) : (
          <VolunteerSpace slug={slug} />
        )}
      </HelpProvider>
    </EventContext.Provider>
  )
}
